import { AggregateItem } from "~/modules/token_timeseries/types";
import { VisualizationCard } from "~/modules/visualization/components/VisualizationCard";
import { useState } from "react";
import { useUser } from "~/modules/user/hooks/useUser";
import { formatDate } from "~/utils/functions/formatDate";
import { NetFlowsData } from "~/modules/fund_flows/api/queryNetFlows";
import { TimeframeSelect } from "~/components/TimeframeSelect";
import { TableToggle } from "~/components/TableToggle";
import { NetFlowsChart } from "./component";
import { ColumnSpanConfig } from "~/types";
import { WindowResizeObserver } from "~/components/WindowResizeObserver";
import type { NetFlowsItemLabels } from "./component";
import type { ReactNode } from "react";
import { RenderWhenVisible } from "~/components/RenderWhenVisible";

// // // //

export function NetFlowsCard(props: {
    title?: string;
    netFlows: NetFlowsData;
    netFlowsWithStablecoins?: NetFlowsData;
    itemLabels?: NetFlowsItemLabels;
    legendWidth?: number;
    height?: number;
    headerControls?: ReactNode;
    cardClassName?: string;
    showInfoTooltip?: boolean;
    colSpans?: ColumnSpanConfig;
    onChangeTimeframe?: (timeframe: string) => void;
    onChangeIncludeStablecoins?: (includeStablecoins: boolean) => void;
}) {
    const {
        title = "Top Net Flows",
        netFlows,
        netFlowsWithStablecoins = null,
        itemLabels,
        legendWidth,
        height = 390,
        headerControls = null,
        cardClassName = "lg:order-0 2xl:order-last",
        showInfoTooltip = true,
        colSpans = {
            sm: 12,
            md: 12,
            lg: 12,
            xl: 6,
            xxl: 6,
        },
        onChangeTimeframe,
        onChangeIncludeStablecoins,
    } = props;
    const { isLoggedIn } = useUser();

    const [timeframe, setTimeframe] = useState<string>("30D");
    const [includeStablecoins, setIncludeStablecoins] =
        useState<boolean>(false);

    const stablecoinsToggle =
        netFlowsWithStablecoins !== null ? (
            <TableToggle
                className="mt-2 mb-2"
                checked={includeStablecoins}
                label="Include Stablecoins"
                onChange={(updatedValue) => {
                    setIncludeStablecoins(updatedValue);

                    if (onChangeIncludeStablecoins) {
                        onChangeIncludeStablecoins(updatedValue);
                    }
                }}
            />
        ) : null;

    const customControls = (
        <div className="w-full flex items-center justify-start sm:justify-end gap-4 md:w-auto md:shrink-0 md:ml-4">
            {headerControls}
            {stablecoinsToggle}

            <TimeframeSelect
                value={timeframe}
                disabled={!isLoggedIn}
                isLoggedIn={isLoggedIn}
                size="sm"
                onChange={(updatedValue) => {
                    setTimeframe(updatedValue);

                    if (onChangeTimeframe) {
                        onChangeTimeframe(updatedValue);
                    }
                }}
            />
        </div>
    );

    // Switch between net flows with and without stablecoins
    const data =
        includeStablecoins && netFlowsWithStablecoins !== null
            ? netFlowsWithStablecoins
            : netFlows;
    const mappedNetFlows: Record<string, AggregateItem[]> = {
        "7D": data.seven_days,
        "30D": data.thirty_days,
        YTD: data.year_to_date,
        "90D": data.ninety_days,
        "6M": data.six_months,
        "1Y": data.one_year,
    };

    const selectedNetFlows: AggregateItem[] = mappedNetFlows[timeframe];

    return (
        <VisualizationCard
            title={title}
            infoTooltip={
                showInfoTooltip ? (
                    <>
                        Net flows are calculated as the difference between
                        tokens minted and tokens burned.
                    </>
                ) : undefined
            }
            subtitle={`as of ${formatDate(new Date())}`}
            cardClassName={cardClassName}
            colSpans={colSpans}
            watermark="center-40pct"
            wrapperClassNameOverride=""
            showTimestamp={false}
            customControls={customControls}
        >
            <RenderWhenVisible placeholderHeight={height}>
                <WindowResizeObserver
                    defaultPlaceholderHeight={height}
                    delay={1200}
                >
                    <NetFlowsChart
                        items={selectedNetFlows}
                        itemLabels={itemLabels}
                        legendWidth={legendWidth}
                        height={height}
                        showIcon={true}
                    />
                </WindowResizeObserver>
            </RenderWhenVisible>
        </VisualizationCard>
    );
}
